import { useCallback, useEffect, useState } from "react";

import { api, ApiClientError } from "../api.js";
import { DataTable } from "../components/data-table.js";
import { MetricCard } from "../components/metric-card.js";
import {
  EmptyState,
  ErrorState,
  LoadingState
} from "../components/state.js";
import {
  formatBytes,
  formatDate,
  formatNumber,
  resultLabel
} from "../format.js";
import type { SystemStatus } from "../types.js";

export function SystemPage({
  onUnauthorized
}: {
  onUnauthorized(): void;
}) {
  const [data, setData] = useState<SystemStatus | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      setData(await api.system());
    } catch (caught) {
      if (caught instanceof ApiClientError && caught.status === 401) {
        onUnauthorized();
        return;
      }
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [onUnauthorized]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (!data && !error) {
    return <LoadingState label="ДИАГНОСТИКА СЕРВЕРА" />;
  }
  if (!data && error) {
    return <ErrorState message={error} onRetry={() => void load()} />;
  }
  if (!data) {
    return null;
  }
  const usage =
    (data.database.total / data.storage.budget_bytes) * 100;
  return (
    <div className="page-stack">
      <section
        className={`system-banner system-banner--${data.status}`}
      >
        <div>
          <span className={`status-dot status-dot--${data.status === "healthy" ? "good" : "bad"}`} />
          СОСТОЯНИЕ СЕРВИСА
        </div>
        <strong>{resultLabel(data.status)}</strong>
        <span>LIVE ENDPOINT НЕ ЗАВИСИТ ОТ ФОНОВЫХ JOB</span>
      </section>
      <section className="metric-grid metric-grid--four">
        <MetricCard
          label="SQLITE TOTAL"
          value={formatBytes(data.database.total)}
          note={`${usage.toFixed(1)}% ОТ ${formatBytes(
            data.storage.budget_bytes
          )}`}
          tone={usage >= 80 ? "warn" : "good"}
        />
        <MetricCard
          label="DATABASE"
          value={formatBytes(data.database.database)}
          note="TELEMETRY.DB"
        />
        <MetricCard
          label="WAL"
          value={formatBytes(data.database.wal)}
          note="WRITE-AHEAD LOG"
          tone={data.database.wal > 100 * 1024 * 1024 ? "warn" : "neutral"}
        />
        <MetricCard
          label="SHARED MEMORY"
          value={formatBytes(data.database.shm)}
          note="SQLITE SHM"
        />
      </section>
      <section className="storage-meter">
        <div style={{ width: `${Math.min(100, usage)}%` }} />
        <span>0</span>
        <span>{formatBytes(data.storage.cleanup_target_bytes)} TARGET</span>
        <span>{formatBytes(data.storage.cleanup_trigger_bytes)} TRIGGER</span>
        <span>{formatBytes(data.storage.budget_bytes)} LIMIT</span>
      </section>
      <section className="panel">
        <header className="panel__header">
          <div>
            <h2>SOURCE MAPS</h2>
          </div>
          <span className="panel__meta">
            {data.source_maps.enabled ? "ENABLED" : "DISABLED"}
          </span>
        </header>
        {data.source_maps.versions.length ? (
          <DataTable headings={["VERSION", "ASSETS", "SIZE", "LAST UPLOAD"]}>
            {data.source_maps.versions.map((version) => (
              <tr key={version.launcher_version}>
                <td className="mono">{version.launcher_version}</td>
                <td className="mono">{formatNumber(version.asset_count)}</td>
                <td className="mono">{formatBytes(version.total_bytes)}</td>
                <td className="mono">{formatDate(version.last_uploaded_at)}</td>
              </tr>
            ))}
          </DataTable>
        ) : (
          <EmptyState>SOURCE MAPS NOT UPLOADED</EmptyState>
        )}
      </section>
      <section className="dashboard-grid">
        <article className="panel">
          <header className="panel__header">
            <div>
              <h2>INGEST COUNTERS</h2>
            </div>
          </header>
          {data.ingest_counters.length ? (
            <div className="counter-list">
              {data.ingest_counters.map((counter) => (
                <div key={counter.name}>
                  <span>{counter.name.replaceAll("_", " ").toUpperCase()}</span>
                  <strong>{formatNumber(counter.value)}</strong>
                  <time>{formatDate(counter.updated_at)}</time>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState>СЧЁТЧИКИ ПОКА НЕ СОЗДАНЫ</EmptyState>
          )}
        </article>
        <article className="panel">
          <header className="panel__header">
            <div>
              <h2>ПУТЬ ХРАНИЛИЩА</h2>
            </div>
          </header>
          <div className="path-block">{data.database.path}</div>
          <dl className="system-facts">
            <div>
              <dt>JOURNAL</dt>
              <dd>WAL</dd>
            </div>
            <div>
              <dt>AUTO VACUUM</dt>
              <dd>INCREMENTAL</dd>
            </div>
            <div>
              <dt>BUSY TIMEOUT</dt>
              <dd>5000 MS</dd>
            </div>
            <div>
              <dt>AUTH DATA</dt>
              <dd>IN MEMORY</dd>
            </div>
          </dl>
        </article>
        <article className="panel panel--full">
          <header className="panel__header">
            <div>
              <h2>ФОНОВЫЕ ЗАДАЧИ И РОТАЦИЯ</h2>
            </div>
            <span className="panel__meta">{data.jobs.length} ЗАПИСЕЙ</span>
          </header>
          {data.jobs.length ? (
            <DataTable
              headings={[
                "СТАРТ",
                "ЗАДАЧА",
                "ПРИЧИНА",
                "СТАТУС",
                "УДАЛЕНО",
                "ДО",
                "ПОСЛЕ",
                "EMERGENCY"
              ]}
            >
              {data.jobs.map((job) => (
                <tr key={job.id}>
                  <td className="mono">{formatDate(job.started_at)}</td>
                  <td>{job.job_name.toUpperCase()}</td>
                  <td>{job.reason.toUpperCase()}</td>
                  <td>
                    <span
                      className={`tag ${
                        job.status === "ok"
                          ? "tag--success"
                          : job.status === "failed"
                            ? "tag--failed"
                            : ""
                      }`}
                    >
                      {resultLabel(job.status)}
                    </span>
                  </td>
                  <td className="mono">{job.deleted_rows}</td>
                  <td className="mono">{formatBytes(job.bytes_before)}</td>
                  <td className="mono">{formatBytes(job.bytes_after)}</td>
                  <td>{job.emergency ? "YES" : "NO"}</td>
                </tr>
              ))}
            </DataTable>
          ) : (
            <EmptyState>ФОНОВЫЕ ЗАДАЧИ ЕЩЁ НЕ ЗАПУСКАЛИСЬ</EmptyState>
          )}
        </article>
      </section>
    </div>
  );
}
